import * as React from 'react';
import { cn } from '../../lib/utils';
import { Button } from './Button';

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

/**
 * Модальное окно: role="dialog", закрытие по Escape и клику по фону,
 * фокус переводится внутрь и удерживается (focus trap), при закрытии
 * возвращается на элемент, который открыл модалку.
 */
function Modal({ open = true, onClose, title, subtitle, icon, size = 'md', footer, children, className }) {
  const panelRef = React.useRef(null);
  const previouslyFocused = React.useRef(null);
  const titleId = React.useId();

  React.useEffect(() => {
    if (!open) return undefined;
    previouslyFocused.current = document.activeElement;

    const panel = panelRef.current;
    const first = panel?.querySelector(FOCUSABLE);
    (first || panel)?.focus();

    function onKeyDown(e) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose?.();
        return;
      }
      if (e.key !== 'Tab' || !panel) return;
      const items = Array.from(panel.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null
      );
      if (items.length === 0) return;
      const firstItem = items[0];
      const lastItem = items[items.length - 1];
      if (e.shiftKey && document.activeElement === firstItem) {
        e.preventDefault();
        lastItem.focus();
      } else if (!e.shiftKey && document.activeElement === lastItem) {
        e.preventDefault();
        firstItem.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown, true);
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.body.style.overflow = overflow;
      previouslyFocused.current?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  const sizes = {
    sm: 'max-w-[420px]',
    md: 'max-w-[560px]',
    lg: 'max-w-[760px]',
    xl: 'max-w-[980px]',
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-xl bg-black/50 backdrop-blur-[2px] animate-fade-in"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
        className={cn(
          'w-full bg-surface-raised border border-border rounded-xl shadow-xl',
          'flex flex-col max-h-[85vh] animate-scale-in outline-none',
          sizes[size],
          className
        )}
      >
        {title && (
          <div className="flex items-start justify-between gap-lg px-2xl py-xl border-b border-border">
            <div className="min-w-0">
              <h2 id={titleId} className="text-section-title font-semibold text-text-strong flex items-center gap-md">
                {icon && <i className={cn('bi', icon, 'text-primary')} aria-hidden="true" />}
                {title}
              </h2>
              {subtitle && <p className="mt-xs text-body-xs text-text-muted">{subtitle}</p>}
            </div>
            <Button variant="plain" size="icon" icon="bi-x-lg" onClick={onClose} title="Закрыть" aria-label="Закрыть" />
          </div>
        )}

        <div className="px-2xl py-xl overflow-y-auto">{children}</div>

        {footer && (
          <div className="px-2xl py-xl border-t border-border flex items-center justify-end gap-md">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

export { Modal };
